import {
  Resource_AppointmentLocation,
  Resource_AppointmentTypeCapability,
  Resource_AppointmentTypeMsg,
  Resource_AppointmentTypeStatus,
  useListAppointmentTypesQuery,
} from '@/generated/graphql';
import { InfoCircleOutlined, TeamOutlined, WarningOutlined } from '@ant-design/icons';
import { Card, Col, Row, Space, Tooltip } from 'antd';
import Title from 'antd/lib/typography/Title';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useRouter } from 'next/router';
import { FC, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { AppointmentLocationTitles } from '../../helpers/enum-maps';
import { extractTranslation } from '../../helpers/formatters';
import { getAppointmentTypeName } from '../../helpers/utils';
import { AveliosSpinner } from '../loading/avelios-spinner';
import styles from './facility-appointment-type.module.scss';

interface FacilityAppointmentTypesProps {
  clinicId: Maybe<string> | undefined;
  formRevisionGroupId: Maybe<string> | undefined;
  officeHourId: Maybe<string> | undefined;
  selectedAppointmentType: Maybe<Resource_AppointmentTypeMsg> | undefined;
  setSelectedAppointmentType: (value: Maybe<Resource_AppointmentTypeMsg> | undefined) => void;
  selectedAppointmentLocation: Maybe<Resource_AppointmentLocation> | undefined;
  setSelectedAppointmentLocation: (value: Maybe<Resource_AppointmentLocation> | undefined) => void;
}

export const FacilityAppointmentTypes: FC<FacilityAppointmentTypesProps> = ({
  officeHourId,
  clinicId,
  selectedAppointmentType,
  setSelectedAppointmentType,
  selectedAppointmentLocation,
  setSelectedAppointmentLocation,
}) => {
  const { formatMessage } = useIntl();
  const { query } = useRouter();

  const queryAppointmentTypeId = query.appointmentTypeId;
  const queryAppointmentLocation = query.appointmentLocation as
    | Resource_AppointmentLocation
    | undefined;

  const { data: appointmentTypesData, loading } = useListAppointmentTypesQuery({
    skip: !clinicId || !officeHourId,
    variables: {
      clinicId: clinicId!,
      request: {
        officeHourId: officeHourId,
        statuses: [Resource_AppointmentTypeStatus.AppointmentTypeStatusActive],
        appointmentTypeCapabilities: [
          Resource_AppointmentTypeCapability.AppointmentTypeCapabilityAppointmentBooking,
        ],
      },
    },
  });
  const firstRenderRef = useRef<boolean>(true);

  const appointmentTypes =
    appointmentTypesData?.listPortalUserAppointmentTypes?.proxyAppointmentTypes;

  useEffect(() => {
    if (firstRenderRef.current && appointmentTypes) {
      firstRenderRef.current = false;
      const foundProxyAppointmentTypeForQuery = !!queryAppointmentTypeId
        ? appointmentTypes?.find(
            (appointmentType) =>
              appointmentType?.appointmentType?.appointmentType?.id === queryAppointmentTypeId,
          )
        : undefined;

      if (!!foundProxyAppointmentTypeForQuery) {
        setSelectedAppointmentType(
          foundProxyAppointmentTypeForQuery.appointmentType?.appointmentType,
        ); // @angelina: this should trigger a page rerender. Since the rerender never happens the appointment type is not selected
        if (
          queryAppointmentLocation &&
          foundProxyAppointmentTypeForQuery.appointmentType?.appointmentType?.appointmentLocations?.includes(
            queryAppointmentLocation,
          )
        ) {
          setSelectedAppointmentLocation(queryAppointmentLocation);
        } else if (
          foundProxyAppointmentTypeForQuery.appointmentType?.appointmentType?.appointmentLocations
            ?.length === 1
        ) {
          setSelectedAppointmentLocation(
            foundProxyAppointmentTypeForQuery.appointmentType?.appointmentType
              ?.appointmentLocations?.[0],
          );
        }
      } else if (appointmentTypes?.length === 1) {
        const firstOne = appointmentTypes?.[0]?.appointmentType?.appointmentType;
        setSelectedAppointmentType(firstOne);
        if (firstOne?.appointmentLocations?.length === 1) {
          setSelectedAppointmentLocation(firstOne?.appointmentLocations?.[0]);
        }
      }
    }
  }, [appointmentTypes, queryAppointmentTypeId, queryAppointmentLocation]);

  const appointmentLocations = selectedAppointmentType?.appointmentLocations?.filterPresent();

  const router = useRouter();

  return (
    <Card className={styles.appointmentTypes}>
      <AveliosSpinner spinning={loading}>
        <Row gutter={[32, 32]}>
          <Col xs={12} lg={24}>
            <Title level={3}>{formatMessage({ id: 'facility-selection.appointment-types' })}</Title>
            <div style={{ width: '100%', marginTop: 22 }}>
              {!loading && !appointmentTypes?.length ? (
                <div>
                  <div
                    style={{
                      textAlign: 'center',
                      fontSize: '2em',
                      color: 'darkorange',
                    }}
                  >
                    <WarningOutlined />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    {formatMessage({
                      id: 'facility-selection.no-appointment-types',
                    })}
                  </div>
                </div>
              ) : (
                appointmentTypes?.map((type) => {
                  const name = extractTranslation(
                    getAppointmentTypeName(type?.appointmentType?.appointmentType),
                    router.locale,
                  );
                  const description = extractTranslation(
                    type?.appointmentType?.appointmentType?.description,
                    router.locale,
                  );

                  const groupAppointment = type?.appointmentType?.appointmentType?.groupAppointment;
                  const groupAppointmentQuota =
                    type?.appointmentType?.appointmentType?.groupAppointmentQuota;

                  return (
                    <Space
                      key={type?.appointmentType?.appointmentType?.id}
                      onClick={() => {
                        if (
                          selectedAppointmentType?.id === type?.appointmentType?.appointmentType?.id
                        ) {
                          setSelectedAppointmentType(undefined);
                        } else {
                          setSelectedAppointmentType(type?.appointmentType?.appointmentType);
                          const defaultLocation =
                            type?.appointmentType?.appointmentType?.appointmentLocations?.[0];
                          setSelectedAppointmentLocation(defaultLocation);
                        }
                      }}
                      className={
                        selectedAppointmentType?.id === type?.appointmentType?.appointmentType?.id
                          ? styles.appointmentTypeTagSelected
                          : styles.appointmentTypeTag
                      }
                    >
                      {!!description && (
                        <Tooltip
                          title={description}
                          key={type?.appointmentType?.appointmentType?.id}
                        >
                          <InfoCircleOutlined />
                        </Tooltip>
                      )}
                      {groupAppointment && (
                        <Tooltip
                          title={
                            groupAppointmentQuota
                              ? formatMessage(
                                  {
                                    id: 'facility-selection.appointment-types.group-appointment-quota',
                                  },
                                  { quota: groupAppointmentQuota },
                                )
                              : formatMessage({
                                  id: 'facility-selection.appointment-types.group-appointment',
                                })
                          }
                        >
                          <TeamOutlined />
                        </Tooltip>
                      )}
                      {name}
                    </Space>
                  );
                })
              )}
            </div>
          </Col>
          <Col xs={12} lg={24}>
            <Title level={3}>
              {formatMessage({
                id: 'facility-selection.appointment-locations',
              })}
            </Title>
            {selectedAppointmentType ? (
              <div style={{ width: '100%', marginTop: 22 }}>
                {appointmentLocations?.map((location) => {
                  const label = formatMessage({
                    id: AppointmentLocationTitles.get(location),
                  });

                  return (
                    <div
                      key={location}
                      onClick={() => {
                        if (selectedAppointmentLocation === location) {
                          setSelectedAppointmentLocation(undefined);
                        } else {
                          setSelectedAppointmentLocation(location);
                        }
                      }}
                      className={
                        selectedAppointmentLocation === location
                          ? styles.appointmentTypeTagSelected
                          : styles.appointmentTypeTag
                      }
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div>
                {formatMessage({
                  id: 'facility-selection.appointment-locations.select-appointment-type',
                })}
              </div>
            )}
          </Col>
        </Row>
      </AveliosSpinner>
    </Card>
  );
};
